import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Building2,
  CalendarDays,
  Calendar,
  Check,
  CheckCircle2,
  CheckSquare,
  ChevronRight,
  FolderKanban,
  GitBranch,
  History,
  Layers,
  MessageSquare,
  Package,
  Paperclip,
  Pencil,
  Send,
  Target,
  Trash2,
  User as UserIcon,
  X,
  Zap,
} from "lucide-react";
import { AppLayout } from "@/components/layout/AppLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { ReferenceIdBadge } from "@/components/ui/ReferenceIdBadge";
import { ConfirmDialog } from "@/components/shared/ConfirmDialog";
import { ActivityHistoryDrawer } from "@/components/audit/ActivityHistoryDrawer";
import { PhaseStagePicker } from "@/components/shared/PhaseStagePicker";
import { AssociationFields } from "@/components/shared/AssociationFields";
import { useAuth } from "@/modules/auth/hooks/useAuth";
import { useUserList } from "@/modules/users/hooks/useUsers";
import {
  useAction,
  useActionComments,
  useAddActionComment,
  useDeleteAction,
  useDeleteActionComment,
  useUpdateAction,
  useUpdateActionStatus,
} from "../hooks/useActions";
import { ActionPriorityBadge } from "../components/ActionPriorityBadge";
import { ActionStatusBadge } from "../components/ActionStatusBadge";
import { ActionSourceBadge } from "../components/ActionSourceBadge";
import { NotesTab } from "@/modules/notes/components/NotesTab";
import { IdeasTab } from "@/modules/ideas/components/IdeasTab";
import { DecisionsTab } from "@/modules/decisions/components/DecisionsTab";
import { NewMilestoneDrawer } from "@/modules/milestones/components/NewMilestoneDrawer";
import { FileUploadZone } from "@/modules/media/components/FileUploadZone";
import type { ActionDetail, ActionPriority, ActionSource, ActionStatus } from "../types";

const STATUS_LIST: { value: ActionStatus; label: string }[] = [
  { value: "OPEN", label: "Open" },
  { value: "IN_PROGRESS", label: "In Progress" },
  { value: "COMPLETED", label: "Completed" },
  { value: "CANCELLED", label: "Cancelled" },
];

type ItemTab = "all" | "notes" | "ideas" | "decisions" | "milestones";

const ITEM_TABS: { key: ItemTab; label: string }[] = [
  { key: "all", label: "All" },
  { key: "notes", label: "Notes" },
  { key: "ideas", label: "Ideas" },
  { key: "decisions", label: "Decisions" },
  { key: "milestones", label: "Milestones" },
];

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function formatDateTime(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function timeAgo(iso: string): string {
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)} min ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} hr ago`;
  if (diff < 2592000) return `${Math.floor(diff / 86400)}d ago`;
  return new Date(iso).toLocaleDateString();
}

function initialsOf(name: string): string {
  return name
    .split(" ")
    .map((n) => n[0])
    .filter(Boolean)
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

function isOverdue(a: ActionDetail): boolean {
  return (
    !!a.dueDate &&
    new Date(a.dueDate) < new Date() &&
    a.status !== "COMPLETED" &&
    a.status !== "CANCELLED"
  );
}

export default function ActionDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();

  const { data: action, isLoading } = useAction(id ?? "");
  const updateAction = useUpdateAction(id ?? "");
  const updateStatus = useUpdateActionStatus();
  const deleteAction = useDeleteAction();
  const commentsQuery = useActionComments(id ?? "");
  const addComment = useAddActionComment(id ?? "");
  const deleteComment = useDeleteActionComment(id ?? "");
  const { data: usersResult } = useUserList({ page: 1, limit: 100 });
  const allUsers = usersResult?.data ?? [];

  const [editMode, setEditMode] = useState(false);
  const [activityOpen, setActivityOpen] = useState(false);
  const [tab, setTab] = useState<ItemTab>("all");
  const [commentText, setCommentText] = useState("");
  const [toast, setToast] = useState<string | null>(null);
  const [milestoneDrawerOpen, setMilestoneDrawerOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [allDoneBanner, setAllDoneBanner] = useState<
    { taskId: string; taskCode: string } | null
  >(null);

  const [editTitle, setEditTitle] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [editPriority, setEditPriority] = useState<ActionPriority>("MEDIUM");
  const [editStatus, setEditStatus] = useState<ActionStatus>("OPEN");
  const [editSource, setEditSource] = useState<ActionSource>("MANUAL");
  const [editAssignedToId, setEditAssignedToId] = useState<string>("");
  const [editPhaseId, setEditPhaseId] = useState<string | null>(null);
  const [editStageId, setEditStageId] = useState<string | null>(null);
  const [editDueDate, setEditDueDate] = useState("");
  const [editCompanyId, setEditCompanyId] = useState<string | null>(null);
  const [editProductId, setEditProductId] = useState<string | null>(null);
  const [editProjectId, setEditProjectId] = useState<string | null>(null);
  const [editMeetingId, setEditMeetingId] = useState<string | null>(null);
  const [editTaskId, setEditTaskId] = useState<string | null>(null);
  const [editDecisionId, setEditDecisionId] = useState<string | null>(null);
  const [editMilestoneId, setEditMilestoneId] = useState<string | null>(null);

  function enterEdit(a: ActionDetail) {
    setEditTitle(a.title);
    setEditDescription(a.description ?? "");
    setEditPriority(a.priority);
    setEditStatus(a.status);
    setEditSource(a.source);
    setEditAssignedToId(a.assignedToId ?? "");
    setEditPhaseId(a.phaseId);
    setEditStageId(a.stageId);
    setEditDueDate(a.dueDate ? a.dueDate.slice(0, 10) : "");
    setEditCompanyId(a.companyId ?? null);
    setEditProductId(a.productId ?? null);
    setEditProjectId(a.projectId ?? null);
    setEditMeetingId(a.meetingId ?? null);
    setEditTaskId(a.taskId ?? null);
    setEditDecisionId(a.decisionId ?? null);
    setEditMilestoneId(a.milestoneId ?? null);
    setEditMode(true);
  }

  async function saveEdit() {
    if (!action) return;
    await updateAction.mutateAsync({
      title: editTitle.trim(),
      description: editDescription.trim() || null,
      priority: editPriority,
      status: editStatus,
      source: editSource,
      assignedToId: editAssignedToId || null,
      phaseId: editPhaseId,
      stageId: editStageId,
      dueDate: editDueDate ? new Date(editDueDate).toISOString() : null,
      companyId: editCompanyId,
      productId: editProductId,
      projectId: editProjectId,
      meetingId: editMeetingId,
      taskId: editTaskId,
      decisionId: editDecisionId,
      milestoneId: editMilestoneId,
    });
    setEditMode(false);
    showToast("Action updated");
  }

  function showToast(msg: string) {
    setToast(msg);
    setTimeout(() => setToast(null), 3500);
  }

  async function handleQuickStatus(newStatus: ActionStatus) {
    if (!action) return;
    const res = await updateStatus.mutateAsync({
      id: action.id,
      status: newStatus,
    });
    if (res.allTaskActionsDone && res.taskId && res.taskCode) {
      setAllDoneBanner({ taskId: res.taskId, taskCode: res.taskCode });
      showToast(
        `All actions done for ${res.taskCode} — task can now be completed`,
      );
    } else {
      showToast(`Status changed to ${newStatus.replace(/_/g, " ")}`);
    }
  }

  function handleDelete() {
    if (!action) return;
    setDeleteOpen(true);
  }

  async function confirmDelete() {
    if (!action) return;
    await deleteAction.mutateAsync(action.id);
    navigate("/actions");
  }

  async function submitComment() {
    if (!commentText.trim()) return;
    await addComment.mutateAsync(commentText.trim());
    setCommentText("");
  }

  if (isLoading || !action) {
    return (
      <AppLayout
        breadcrumb={[
          { label: "PhazeX Hub", href: "/" },
          { label: "Daily Work" },
          { label: "Actions", href: "/actions" },
        ]}
      >
        <div className="py-12 text-center text-muted-foreground">
          {isLoading ? "Loading…" : "Action not found"}
        </div>
      </AppLayout>
    );
  }

  const overdue = isOverdue(action);

  const contextChain: {
    label: string;
    href?: string;
    icon: typeof Building2;
  }[] = [];
  if (action.companyId)
    contextChain.push({
      label: action.companyName ?? action.companyId,
      href: `/companies/${action.companyId}`,
      icon: Building2,
    });
  if (action.productId)
    contextChain.push({
      label: action.productName ?? action.productId,
      href: `/products/${action.productId}`,
      icon: Package,
    });
  if (action.projectId)
    contextChain.push({
      label: action.projectName ?? action.projectId,
      href: `/projects/${action.projectId}`,
      icon: FolderKanban,
    });
  if (action.phaseName)
    contextChain.push({ label: action.phaseName, icon: Layers });
  if (action.stageName)
    contextChain.push({ label: action.stageName, icon: GitBranch });
  if (action.meetingId)
    contextChain.push({
      label: action.meetingTitle ?? action.meetingId,
      href: `/meetings/${action.meetingId}`,
      icon: CalendarDays,
    });
  if (action.taskId)
    contextChain.push({
      label: action.taskTitle ?? action.taskId,
      href: `/tasks/${action.taskId}`,
      icon: CheckSquare,
    });
  if (action.decisionId)
    contextChain.push({
      label: action.decisionTitle ?? action.decisionId,
      href: `/decisions/${action.decisionId}`,
      icon: CheckCircle2,
    });
  if (action.milestoneId)
    contextChain.push({
      label: action.milestoneTitle ?? action.milestoneId,
      href: `/milestones/${action.milestoneId}`,
      icon: Target,
    });

  return (
    <AppLayout
      breadcrumb={[
        { label: "PhazeX Hub", href: "/" },
        { label: "Daily Work" },
        { label: "Actions", href: "/actions" },
        { label: action.code },
      ]}
    >
      {toast && (
        <div
          className="fixed right-6 top-16 z-[9999] rounded-lg border bg-white px-4 py-3 text-sm font-medium text-green-700 shadow-lg"
          style={{ borderColor: "hsl(142,55%,80%)" }}
        >
          {toast}
        </div>
      )}

      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => navigate("/actions")}
            className="flex h-8 w-8 items-center justify-center rounded hover:bg-[hsl(210,50%,96%)]"
          >
            <ArrowLeft className="h-4 w-4" strokeWidth={1.5} />
          </button>
          <Zap
            className="h-[18px] w-[18px] text-foreground"
            strokeWidth={1.5}
          />
          <h1 className="text-xl font-semibold text-foreground">View Action</h1>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setActivityOpen(true)}
            className="flex h-8 w-8 items-center justify-center rounded hover:bg-[hsl(210,50%,96%)]"
            title="History"
          >
            <History
              className="h-4 w-4 text-muted-foreground"
              strokeWidth={1.5}
            />
          </button>
          <button
            type="button"
            onClick={() => (editMode ? setEditMode(false) : enterEdit(action))}
            className="flex h-8 w-8 items-center justify-center rounded hover:bg-[hsl(210,50%,96%)]"
            title="Edit"
          >
            <Pencil
              className="h-4 w-4 text-muted-foreground"
              strokeWidth={1.5}
            />
          </button>
          <button
            type="button"
            onClick={handleDelete}
            className="flex h-8 w-8 items-center justify-center rounded hover:bg-[hsl(0,70%,96%)]"
            title="Delete"
          >
            <Trash2
              className="h-4 w-4 text-[hsl(0,70%,45%)]"
              strokeWidth={1.5}
            />
          </button>
        </div>
      </div>

      {/* ROW 1 — Summary Header */}
      <div
        className="mb-4 rounded-xl border bg-white"
        style={{ borderColor: "hsl(210,30%,85%)", padding: "16px 20px" }}
      >
        {editMode ? (
          <Input
            value={editTitle}
            onChange={(e) => setEditTitle(e.target.value)}
            className="text-[20px] font-bold"
          />
        ) : (
          <div className="text-[20px] font-bold text-foreground">
            {action.title}
          </div>
        )}
        <div className="mt-2 flex flex-wrap items-center gap-2 text-[13px] text-muted-foreground">
          <ReferenceIdBadge id={action.code} />
          <ActionStatusBadge status={action.status} />
          <ActionPriorityBadge priority={action.priority} />
          <ActionSourceBadge source={action.source} />
          {action.projectName && (
            <span className="inline-flex items-center gap-1">
              <FolderKanban className="h-3 w-3" strokeWidth={1.5} />
              {action.projectName}
            </span>
          )}
          {action.phaseName && (
            <span
              className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px]"
              style={{ background: "hsl(210,16%,95%)" }}
            >
              <Layers className="h-3 w-3" strokeWidth={1.5} />
              {action.phaseName}
            </span>
          )}
          {action.taskCode && (
            <Link
              to={`/tasks/${action.taskId}`}
              className="inline-flex items-center gap-1 font-mono text-muted-foreground hover:text-[hsl(210,100%,35%)]"
            >
              ↳ {action.taskCode}
            </Link>
          )}
          {action.assignedToName && (
            <span className="inline-flex items-center gap-1">
              <UserIcon className="h-3 w-3" strokeWidth={1.5} />
              {action.assignedToName}
            </span>
          )}
          {action.dueDate && (
            <span
              className={`inline-flex items-center gap-1 ${
                overdue ? "text-[hsl(0,70%,45%)] font-medium" : ""
              }`}
            >
              <Calendar className="h-3 w-3" strokeWidth={1.5} />
              {formatDate(action.dueDate)}
            </span>
          )}
          {overdue && (
            <span
              className="rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase"
              style={{ background: "hsl(0,70%,94%)", color: "hsl(0,70%,40%)" }}
            >
              Overdue
            </span>
          )}
        </div>
      </div>

      {/* ROW 2 — Basic Info + Dates */}
      <div
        className="mb-4 grid gap-4"
        style={{ gridTemplateColumns: "60fr 40fr" }}
      >
        <div
          className="rounded-xl border bg-white"
          style={{ borderColor: "hsl(210,30%,85%)", padding: "16px 20px" }}
        >
          <div className="mb-3 flex items-center justify-between">
            <h3 className="text-[13px] font-semibold text-foreground">
              Basic Info
            </h3>
            {editMode ? (
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={saveEdit}
                  className="flex h-7 w-7 items-center justify-center rounded text-[hsl(142,72%,28%)] hover:bg-[hsl(142,55%,94%)]"
                  title="Save"
                >
                  <Check className="h-4 w-4" strokeWidth={1.5} />
                </button>
                <button
                  type="button"
                  onClick={() => setEditMode(false)}
                  className="flex h-7 w-7 items-center justify-center rounded text-muted-foreground hover:bg-[hsl(210,50%,96%)]"
                  title="Cancel"
                >
                  <X className="h-4 w-4" strokeWidth={1.5} />
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => enterEdit(action)}
                className="flex h-7 w-7 items-center justify-center rounded hover:bg-[hsl(210,50%,96%)]"
                title="Edit"
              >
                <Pencil
                  className="h-3.5 w-3.5 text-muted-foreground"
                  strokeWidth={1.5}
                />
              </button>
            )}
          </div>

          <div className="mb-4 flex flex-wrap items-center gap-1.5 text-[12px] text-muted-foreground">
            <span className="font-medium">PhazeX</span>
            {contextChain.length === 0 ? (
              <>
                <ChevronRight className="h-3 w-3" strokeWidth={1.5} />
                <span>—</span>
              </>
            ) : (
              contextChain.map((c, i) => {
                const Icon = c.icon;
                return (
                  <span key={i} className="inline-flex items-center gap-1.5">
                    <ChevronRight className="h-3 w-3" strokeWidth={1.5} />
                    <Icon className="h-3 w-3" strokeWidth={1.5} />
                    {c.href ? (
                      <Link
                        to={c.href}
                        className="hover:text-[hsl(210,100%,35%)]"
                      >
                        {c.label}
                      </Link>
                    ) : (
                      <span>{c.label}</span>
                    )}
                  </span>
                );
              })
            )}
          </div>

          <div
            className="mb-4 rounded-lg border bg-[hsl(210,30%,98%)] p-3"
            style={{ borderColor: "hsl(210,30%,88%)" }}
          >
            <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              Linked Context
            </div>
            <div className="grid grid-cols-2 gap-y-1.5 text-[12px]">
              <LinkedRow
                label="Company"
                value={action.companyName}
                href={
                  action.companyId
                    ? `/companies/${action.companyId}`
                    : null
                }
              />
              <LinkedRow
                label="Product"
                value={action.productName}
                href={
                  action.productId
                    ? `/products/${action.productId}`
                    : null
                }
              />
              <LinkedRow
                label="Project"
                value={action.projectName}
                href={
                  action.projectId
                    ? `/projects/${action.projectId}`
                    : null
                }
              />
              <LinkedRow
                label="Meeting"
                value={
                  action.meetingTitle
                    ? `${action.meetingCode ?? ""} ${action.meetingTitle}`.trim()
                    : null
                }
                href={
                  action.meetingId
                    ? `/meetings/${action.meetingId}`
                    : null
                }
              />
              <LinkedRow
                label="Task"
                value={
                  action.taskTitle
                    ? `${action.taskCode ?? ""} ${action.taskTitle}`.trim()
                    : null
                }
                href={action.taskId ? `/tasks/${action.taskId}` : null}
              />
              <LinkedRow
                label="Decision"
                value={
                  action.decisionTitle
                    ? `${action.decisionCode ?? ""} ${action.decisionTitle}`.trim()
                    : null
                }
                href={
                  action.decisionId
                    ? `/decisions/${action.decisionId}`
                    : null
                }
              />
              <LinkedRow
                label="Milestone"
                value={
                  action.milestoneTitle
                    ? `${action.milestoneCode ?? ""} ${action.milestoneTitle}`.trim()
                    : null
                }
                href={
                  action.milestoneId
                    ? `/milestones/${action.milestoneId}`
                    : null
                }
              />
            </div>
          </div>

          {(action.meetingId || action.taskId) && (
            <div className="mb-4 flex flex-wrap items-center gap-3 text-[12px]">
              {action.meetingId && (
                <Link
                  to={`/meetings/${action.meetingId}`}
                  className="inline-flex items-center gap-1.5 rounded border px-2 py-1 text-muted-foreground hover:text-[hsl(210,100%,35%)]"
                  style={{ borderColor: "hsl(210,30%,85%)" }}
                >
                  <CalendarDays className="h-3 w-3" strokeWidth={1.5} />
                  {action.meetingTitle ?? "Meeting"}
                </Link>
              )}
              {action.taskId && (
                <Link
                  to={`/tasks/${action.taskId}`}
                  className="inline-flex items-center gap-1.5 rounded border px-2 py-1 text-muted-foreground hover:text-[hsl(210,100%,35%)]"
                  style={{ borderColor: "hsl(210,30%,85%)" }}
                >
                  <CheckSquare className="h-3 w-3" strokeWidth={1.5} />
                  <span className="font-mono">{action.taskCode}</span>
                  {action.taskTitle ? ` — ${action.taskTitle}` : ""}
                </Link>
              )}
            </div>
          )}

          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <div className="text-[11px] uppercase text-muted-foreground">
                Owner
              </div>
              {editMode ? (
                <select
                  value={editAssignedToId}
                  onChange={(e) => setEditAssignedToId(e.target.value)}
                  className="mt-1 flex h-8 w-full rounded-md border border-border bg-background px-2 py-1 text-sm"
                >
                  <option value="">Unassigned</option>
                  {allUsers.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.name}
                    </option>
                  ))}
                </select>
              ) : (
                <div className="mt-0.5 text-foreground">
                  {action.assignedToName ?? "Unassigned"}
                </div>
              )}
            </div>
            <div>
              <div className="text-[11px] uppercase text-muted-foreground">
                Status
              </div>
              {editMode ? (
                <Select
                  value={editStatus}
                  onChange={(e) => setEditStatus(e.target.value as ActionStatus)}
                >
                  <option value="OPEN">Open</option>
                  <option value="IN_PROGRESS">In Progress</option>
                  <option value="COMPLETED">Completed</option>
                  <option value="CANCELLED">Cancelled</option>
                </Select>
              ) : (
                <div className="mt-1">
                  <ActionStatusBadge status={action.status} />
                </div>
              )}
            </div>
            <div>
              <div className="text-[11px] uppercase text-muted-foreground">
                Priority
              </div>
              {editMode ? (
                <Select
                  value={editPriority}
                  onChange={(e) =>
                    setEditPriority(e.target.value as ActionPriority)
                  }
                >
                  <option value="CRITICAL">Critical</option>
                  <option value="HIGH">High</option>
                  <option value="MEDIUM">Medium</option>
                  <option value="LOW">Low</option>
                </Select>
              ) : (
                <div className="mt-1">
                  <ActionPriorityBadge priority={action.priority} />
                </div>
              )}
            </div>
            <div>
              <div className="text-[11px] uppercase text-muted-foreground">
                Source
              </div>
              {editMode ? (
                <Select
                  value={editSource}
                  onChange={(e) => setEditSource(e.target.value as ActionSource)}
                >
                  <option value="MANUAL">Manual</option>
                  <option value="MEETING">Meeting</option>
                  <option value="TASK">Task</option>
                </Select>
              ) : (
                <div className="mt-1">
                  <ActionSourceBadge source={action.source} />
                </div>
              )}
            </div>
            <div>
              <div className="text-[11px] uppercase text-muted-foreground">
                Due Date
              </div>
              {editMode ? (
                <Input
                  type="date"
                  value={editDueDate}
                  onChange={(e) => setEditDueDate(e.target.value)}
                />
              ) : (
                <div
                  className={`mt-0.5 ${
                    overdue
                      ? "text-[hsl(0,70%,45%)] font-medium"
                      : "text-foreground"
                  }`}
                >
                  {formatDate(action.dueDate)}
                </div>
              )}
            </div>
            <div>
              <div className="text-[11px] uppercase text-muted-foreground">
                Phase / Stage
              </div>
              {editMode ? (
                <PhaseStagePicker
                  projectId={editProjectId}
                  phaseId={editPhaseId}
                  stageId={editStageId}
                  onPhaseChange={setEditPhaseId}
                  onStageChange={setEditStageId}
                />
              ) : (
                <div className="mt-0.5 text-foreground">
                  {action.phaseName ?? "—"}
                  {action.stageName ? ` / ${action.stageName}` : ""}
                </div>
              )}
            </div>
          </div>

          <div className="mt-4">
            <div className="text-[11px] uppercase text-muted-foreground">
              Description
            </div>
            {editMode ? (
              <textarea
                rows={3}
                value={editDescription}
                onChange={(e) => setEditDescription(e.target.value)}
                className="mt-1 flex w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
              />
            ) : (
              <div className="mt-1 whitespace-pre-wrap text-sm text-foreground">
                {action.description ?? "—"}
              </div>
            )}
          </div>

          {editMode && (
            <div className="mt-4 border-t pt-4" style={{ borderColor: "hsl(210,30%,90%)" }}>
              <AssociationFields
                companyId={editCompanyId}
                productId={editProductId}
                projectId={editProjectId}
                meetingId={editMeetingId}
                taskId={editTaskId}
                decisionId={editDecisionId}
                milestoneId={editMilestoneId}
                showMeeting
                showTask
                showDecision
                showMilestone
                onChange={(field, value) => {
                  if (field === "companyId") setEditCompanyId(value);
                  else if (field === "productId") setEditProductId(value);
                  else if (field === "projectId") {
                    setEditProjectId(value);
                    setEditPhaseId(null);
                    setEditStageId(null);
                  } else if (field === "meetingId") setEditMeetingId(value);
                  else if (field === "taskId") setEditTaskId(value);
                  else if (field === "decisionId") setEditDecisionId(value);
                  else if (field === "milestoneId") setEditMilestoneId(value);
                }}
              />
            </div>
          )}
        </div>

        {/* RIGHT — Dates & Quick Status */}
        <div
          className="rounded-xl border bg-white"
          style={{ borderColor: "hsl(210,30%,85%)", padding: "16px 20px" }}
        >
          <h3 className="mb-3 text-[13px] font-semibold text-foreground">
            Dates
          </h3>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <div className="text-[11px] uppercase text-muted-foreground">
                Due Date
              </div>
              <div
                className={`mt-0.5 ${
                  overdue
                    ? "text-[hsl(0,70%,45%)] font-medium"
                    : "text-foreground"
                }`}
              >
                {formatDate(action.dueDate)}
              </div>
            </div>
            <div>
              <div className="text-[11px] uppercase text-muted-foreground">
                Created At
              </div>
              <div className="mt-0.5 text-foreground">
                {formatDateTime(action.createdAt)}
              </div>
            </div>
            <div>
              <div className="text-[11px] uppercase text-muted-foreground">
                Updated At
              </div>
              <div className="mt-0.5 text-foreground">
                {formatDateTime(action.updatedAt)}
              </div>
            </div>
            {action.completedAt && (
              <div>
                <div className="text-[11px] uppercase text-muted-foreground">
                  Completed At
                </div>
                <div className="mt-0.5 text-foreground">
                  {formatDateTime(action.completedAt)}
                </div>
              </div>
            )}
          </div>

          <div
            className="my-4 border-t"
            style={{ borderColor: "hsl(210,30%,90%)" }}
          />

          <div>
            <div className="mb-2 text-[11px] uppercase text-muted-foreground">
              Change Status
            </div>
            <div className="flex flex-wrap gap-2">
              {STATUS_LIST.map((s) => {
                const active = action.status === s.value;
                return (
                  <button
                    key={s.value}
                    type="button"
                    disabled={updateStatus.isPending}
                    onClick={() => handleQuickStatus(s.value)}
                    className="inline-flex h-7 items-center rounded-full border px-3 text-[12px] font-medium transition-colors disabled:cursor-not-allowed"
                    style={{
                      background: active
                        ? "hsl(210,100%,45%)"
                        : "transparent",
                      color: active ? "#fff" : "hsl(215,16%,40%)",
                      borderColor: active
                        ? "hsl(210,100%,45%)"
                        : "hsl(210,30%,85%)",
                    }}
                  >
                    {s.label}
                  </button>
                );
              })}
            </div>
          </div>

          {allDoneBanner && (
            <div
              className="mt-4 rounded-lg border px-4 py-3 text-sm"
              style={{
                background: "hsl(142,55%,94%)",
                borderColor: "hsl(142,55%,75%)",
                color: "hsl(142,72%,22%)",
              }}
            >
              ✓ All actions for{" "}
              <Link
                to={`/tasks/${allDoneBanner.taskId}`}
                className="font-mono font-semibold underline-offset-2 hover:underline"
              >
                {allDoneBanner.taskCode}
              </Link>{" "}
              are done! You can now complete the task.
            </div>
          )}
        </div>
      </div>

      {/* ROW 3 — Items tabs */}
      <div
        className="mb-4 overflow-hidden rounded-xl border bg-white"
        style={{ borderColor: "hsl(210,30%,85%)" }}
      >
        <div
          className="flex items-center border-b"
          style={{ borderColor: "hsl(210,30%,90%)" }}
        >
          {ITEM_TABS.map((t) => {
            const active = tab === t.key;
            return (
              <button
                key={t.key}
                type="button"
                onClick={() => setTab(t.key)}
                className={`flex items-center gap-1.5 border-b-2 px-4 py-2.5 text-sm font-medium transition-colors ${
                  active
                    ? "border-primary text-primary"
                    : "border-transparent text-muted-foreground"
                }`}
              >
                {t.label}
                <span
                  className="inline-flex h-5 min-w-[20px] items-center justify-center rounded-full px-1.5 text-[10px] font-medium"
                  style={{
                    background: active
                      ? "hsl(210,55%,92%)"
                      : "hsl(210,16%,93%)",
                    color: active
                      ? "hsl(210,100%,28%)"
                      : "hsl(215,16%,40%)",
                  }}
                >
                  0
                </span>
              </button>
            );
          })}
        </div>

        <div className="p-4">
          {tab === "all" && (
            <div className="py-8 text-center text-sm text-muted-foreground">
              No items yet
            </div>
          )}
          {tab === "notes" && (
            <NotesTab
              filter={{ actionId: action.id }}
              prefill={{
                actionId: action.id,
                actionTitle: action.title,
                actionCode: action.code,
                companyId: action.companyId,
                productId: action.productId,
                projectId: action.projectId,
                locked: { actionId: true },
              }}
            />
          )}
          {tab === "ideas" && (
            <IdeasTab
              filter={{
                meetingId: action.meetingId ?? undefined,
                projectId: action.meetingId
                  ? undefined
                  : action.projectId ?? undefined,
                companyId:
                  action.meetingId || action.projectId
                    ? undefined
                    : action.companyId ?? undefined,
              }}
              prefill={{
                companyId: action.companyId,
                productId: action.productId,
                projectId: action.projectId,
                meetingId: action.meetingId,
              }}
            />
          )}
          {tab === "decisions" && (
            <DecisionsTab
              filter={{
                meetingId: action.meetingId ?? undefined,
                projectId: action.meetingId
                  ? undefined
                  : action.projectId ?? undefined,
                companyId:
                  action.meetingId || action.projectId
                    ? undefined
                    : action.companyId ?? undefined,
              }}
              prefill={{
                companyId: action.companyId,
                productId: action.productId,
                projectId: action.projectId,
                meetingId: action.meetingId,
              }}
            />
          )}
          {tab === "milestones" && (
            <div className="space-y-3">
              <div className="flex justify-end">
                <Button
                  size="sm"
                  onClick={() => setMilestoneDrawerOpen(true)}
                >
                  + Create Milestone
                </Button>
              </div>
              <div className="py-8 text-center text-sm text-muted-foreground">
                No milestones linked yet
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ROW 4 — Comments + Attachments */}
      <div
        className="grid gap-4"
        style={{ gridTemplateColumns: "60fr 40fr" }}
      >
        <div
          className="flex flex-col rounded-xl border bg-white"
          style={{ borderColor: "hsl(210,30%,85%)", padding: "16px 20px" }}
        >
          <div className="mb-3 flex items-center gap-2">
            <MessageSquare
              className="h-4 w-4 text-foreground"
              strokeWidth={1.5}
            />
            <h3 className="text-[13px] font-semibold text-foreground">
              Comments
            </h3>
            <span
              className="inline-flex h-5 min-w-[20px] items-center justify-center rounded-full px-1.5 text-[10px] font-medium"
              style={{
                background: "hsl(210,16%,93%)",
                color: "hsl(215,16%,40%)",
              }}
            >
              {commentsQuery.data?.length ?? 0}
            </span>
          </div>

          <div className="max-h-[300px] flex-1 overflow-y-auto">
            {(!commentsQuery.data || commentsQuery.data.length === 0) && (
              <div className="py-6 text-center text-sm text-muted-foreground">
                No comments yet. Be the first to comment.
              </div>
            )}
            {[...(commentsQuery.data ?? [])].reverse().map((c) => {
              const isOwn = c.authorId === user?.id;
              return (
                <div
                  key={c.id}
                  className="border-b py-3 last:border-b-0"
                  style={{ borderColor: "hsl(210,30%,93%)" }}
                >
                  <div className="flex items-start gap-2">
                    <span
                      className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[10px] font-semibold"
                      style={{
                        background: "hsl(210,55%,92%)",
                        color: "hsl(210,100%,28%)",
                      }}
                    >
                      {initialsOf(c.authorName ?? "??")}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-[13px] font-semibold text-foreground">
                          {c.authorName ?? "Unknown"}
                        </span>
                        <span className="text-[11px] text-muted-foreground">
                          {timeAgo(c.createdAt)}
                        </span>
                        {isOwn && (
                          <button
                            type="button"
                            onClick={() => deleteComment.mutate(c.id)}
                            className="ml-auto text-muted-foreground hover:text-[hsl(0,70%,45%)]"
                            title="Delete"
                          >
                            <Trash2 className="h-3 w-3" strokeWidth={1.5} />
                          </button>
                        )}
                      </div>
                      <div className="mt-1 whitespace-pre-wrap text-[14px] text-foreground">
                        {c.content}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div
            className="mt-3 flex items-end gap-2 border-t pt-3"
            style={{ borderColor: "hsl(210,30%,90%)" }}
          >
            <textarea
              rows={2}
              value={commentText}
              onChange={(e) => setCommentText(e.target.value)}
              onKeyDown={(e) => {
                if (e.ctrlKey && e.key === "Enter") {
                  e.preventDefault();
                  submitComment();
                }
              }}
              placeholder="Write a comment... (Ctrl+Enter to submit)"
              className="flex-1 rounded-md border border-border bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            />
            <button
              type="button"
              onClick={submitComment}
              disabled={!commentText.trim() || addComment.isPending}
              className="flex h-9 w-9 items-center justify-center rounded-full text-white disabled:opacity-50"
              style={{ background: "hsl(210,100%,45%)" }}
              title="Send"
            >
              <Send className="h-3.5 w-3.5" strokeWidth={1.8} />
            </button>
          </div>
        </div>

        <div
          className="rounded-xl border bg-white"
          style={{ borderColor: "hsl(210,30%,85%)", padding: "16px 20px" }}
        >
          <div className="mb-3 flex items-center gap-2">
            <Paperclip
              className="h-4 w-4 text-foreground"
              strokeWidth={1.5}
            />
            <h3 className="text-[13px] font-semibold text-foreground">
              Attachments
            </h3>
          </div>
          <FileUploadZone
            entityType="action"
            entityId={action.id}
            entityLabel={action.code}
            actionId={action.id}
            companyId={action.companyId ?? undefined}
            productId={action.productId ?? undefined}
            projectId={action.projectId ?? undefined}
            taskId={action.taskId ?? undefined}
            showExisting
            compact
          />
        </div>
      </div>

      <NewMilestoneDrawer
        isOpen={milestoneDrawerOpen}
        onClose={() => setMilestoneDrawerOpen(false)}
        onCreated={(code) => showToast(`Milestone created: ${code}`)}
        prefill={{
          companyId: action.companyId,
          productId: action.productId,
          projectId: action.projectId,
          meetingId: action.meetingId,
        }}
      />

      <ActivityHistoryDrawer
        isOpen={activityOpen}
        onClose={() => setActivityOpen(false)}
        entityType="action"
        entityId={action.id}
        entityLabel={action.title}
      />

      <ConfirmDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        title="Delete action?"
        description={`Are you sure you want to delete '${action.title}'? It will be removed from your Actions list.`}
        onConfirm={confirmDelete}
      />
    </AppLayout>
  );
}

function LinkedRow({
  label,
  value,
  href,
}: {
  label: string;
  value: string | null;
  href?: string | null;
}) {
  return (
    <div className="flex gap-2">
      <span className="w-24 shrink-0 text-muted-foreground">{label}:</span>
      {value ? (
        href ? (
          <Link to={href} className="text-foreground hover:underline">
            {value}
          </Link>
        ) : (
          <span className="text-foreground">{value}</span>
        )
      ) : (
        <span className="text-muted-foreground">—</span>
      )}
    </div>
  );
}
